<template>
  <v-container class="pa-0">
    <v-stepper :value="0">
      <!--Header/Steps-->
      <v-stepper-header>
        <v-stepper-item
          complete
          editable
          :step="0"
          :color="'success'"
        >
          <template #title>
            <div class="step-name-green">
              {{ $t("Summary") }}
            </div>
          </template>
        </v-stepper-item>
      </v-stepper-header>
      <v-divider class="mb-6" />
      <!--Content/Tabs-->
      <v-container class="stepper-content">
        <v-tabs
          v-model="activeTab"
          direction="vertical"
          class="summary-content vertical-slider-line"
        >
          <div class="v-col-2 d-flex">
            <v-tab
              class="tab-header mr-auto"
              disabled
            >
              {{ $t("Review Order") }}
            </v-tab>
          </div>

          <div class="v-col-md-9 v-col-12">
            <v-window>
              <v-window-item>
                <div class="form-title">
                  {{ $t("Review Order") }}
                </div>
                <div class="pa-2" />

                <template v-if="form">
                  <template
                    v-for="step in form.steps"
                    :key="step.id"
                  >
                    <div class="step-name">
                      {{ getStepName(getOrderFormStepBySortOrder(step.sortOrder)) }}
                    </div>
                    <div
                      v-for="tabId in step.tabs"
                      :key="tabId"
                      transition="false"
                    >
                      <template v-if="showTab((getOrderFormTabById(tabId)))">
                        <div
                          :key="tabId"
                          class="tab-name"
                        >
                          {{ getTabName(getOrderFormTabById(tabId)) }}
                        </div>
                        <template
                          v-for="controlId in getOrderFormTabById(tabId).controls"
                        >
                          <div
                            v-if="getOrderFormControlById(controlId).value && showControl((getOrderFormControlById(controlId)))"
                            :key="getOrderFormControlById(controlId).uniqueId"
                          >
                            <div class="control-label">
                              {{
                                removeColon(
                                  getControlName(
                                    getOrderFormControlById(controlId)
                                  ).trim()
                                )
                              }}{{
                                insertColon(getOrderFormControlById(controlId))
                              }}
                              <span
                                v-if="showValue(controlId)"
                                class="control-value"
                              >
                                {{
                                  getControlValue(
                                    getOrderFormControlById(controlId)
                                  )
                                }}
                              </span>
                            </div>
                          </div>
                        </template>
                      </template>
                    </div>
                    <br>
                  </template>
                </template>
                <div class="step-name">
                  {{ $t('Supporting files') }}
                </div>

                <v-data-table
                  v-if="supportingFiles"
                  :headers="headers"
                  :items="supportingFiles"
                  :no-data-text="t('No data available')"
                  class="elevation-1"
                  hide-default-footer
                />
                <div class="d-flex justify-center" v-else >
                  {{ $t("No reports found") }}
                </div>

                <div class="py-5 summary-content download-order-summary">
                  <OrderSummaryDownload />
                </div>
                <v-container class="tab-footer">
                  <Button
                    color="secondary text-red"
                    class="cancel-order-button"
                    @click="cancelOrder()"
                  >
                    {{ $t("Cancel order") }}
                  </Button>
                  <Button
                    color="secondary"
                    class="text-primary"
                    @click="$emit('hideSummary')"
                  >
                    {{
                      $t("Back")
                    }}
                  </Button>
                  <Button
                    class="ml-sm-3"
                    @click="$emit('sumbitOrder')"
                  >
                    {{ $t(submitButtonText) }}
                    <v-icon
                      class="text-white primary icon-flipped"
                      end
                    >
                      mdi-keyboard-backspace
                    </v-icon>
                  </Button>
                </v-container>
              </v-window-item>
            </v-window>
          </div>
        </v-tabs>
      </v-container>
    </v-stepper>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed } from "vue"
import Button from "@/components/input-components/Button.vue"
import OrderSummaryDownload from "@/components/orders/OrderSummaryDownload.vue"
import { JsonForm } from "@/entities/json-form"
import { FormTab } from "@/entities/form-tab"
import { FormStep } from "@/entities/form-step"
import { FormControl } from "@/entities/form-control"
import { Order } from "@/entities/order"
import { ORDER_STATUS } from "@/utils/constants"
import { DROPDOWN, RADIO } from "@/entities/form-control-types"
import { useControlRenderer } from "@/composables/useControlRenderer"
import {formatDisplayDate} from "@/composables/useDateHelper";
import { useOrdersStore } from "@/stores/orders"
import { useI18n } from "vue-i18n"
import { OrderSupportingFile } from "@/entities/order-supporting-file";

defineOptions({ name: "OrderSummary" })

const emit = defineEmits<{
  hideSummary: []
  sumbitOrder: []
  cancelOrder: []
}>()

const ordersStore = useOrdersStore()
const { t, locale } = useI18n()

const activeTab = ref(0)
const headers = computed(() => [
  { title: t("File name"), align: 'start', key: 'fileName', width: '50%', },
  { title: t("Comments"), key: 'comment' },
])
const selectedStepOrder = ref(1)

const { showControl, showTab } = useControlRenderer()

const order = computed<Order>(() => ordersStore.getOrder)
const form = computed<JsonForm>(() => ordersStore.getOrderForm)
const supportingFiles = computed<OrderSupportingFile[]>(() => ordersStore.getOrderSupportingFiles)

const isDraft = computed(() => order.value?.statusId === ORDER_STATUS.draft)
const submitButtonText = computed(() =>
  isDraft.value ? "Submit order" : "Check order info"
)

function cancelOrder() {
  emit("cancelOrder")
}

function removeColon(s: string): string {
  return s.endsWith(":") ? s.slice(0, -1) : s
}

function insertColon(control: FormControl): string {
  if (
    control.value?.toString().toLowerCase() !== "true" &&
    control.label !== ""
  ) {
    return ":"
  }
  return ""
}

function getStepName(step: FormStep): string {
  return isLocalizationAvailable(step)
    ? step.localizations[locale.value].name
    : step.name
}

function getTabName(tab: FormTab): string {
  return isLocalizationAvailable(tab)
    ? tab.localizations[locale.value].name
    : tab.name
}

function getControlName(control: FormControl): string {
  return isLabelLocalizationAvailable(control)
    ? control.localizations[locale.value].label
    : control.label
}

function getControlValue(control: FormControl): string {
  const isList =
    control.type.toLowerCase() === RADIO ||
    control.type.toLowerCase() === DROPDOWN

  if (isList && isLocalizationAvailable(control)) {
    const index = control.items.findIndex(item => item === control.value)
    const localized = control.localizations[locale.value]?.items[index]
    if (localized !== undefined && localized !== null) {
      return localized
    }
  }

  if (control.type === "date") {
    return formatDisplayDate(control.value)
  }

  return control.value
}

function isLabelLocalizationAvailable(obj: FormControl | FormTab): boolean {
  return (
    locale.value !== "en" &&
    obj.localizations[locale.value]?.label !== undefined
  )
}

function isLocalizationAvailable(obj: FormControl | FormTab | FormStep): boolean {
  return (
    locale.value !== "en" &&
    obj.localizations[locale.value] !== undefined
  )
}

function showValue(controlId: string): boolean {
  const control = getOrderFormControlById(controlId)
  if (!control) return false
  return control.value?.toString().toLowerCase() !== "true"
}

const getOrderFormTabById = ordersStore.getOrderFormTabById
const getOrderFormControlById = ordersStore.getOrderFormControlById
const getOrderFormStepBySortOrder = ordersStore.getOrderFormStepBySortOrder
</script>


<style lang="scss">
.summary-content {
  .step-name {
    text-transform: uppercase;
    text-align: left;
    margin: 0.625rem 0;
    font-weight: bold;
    font-size: 1.75rem;
  }

  .tab-name {
    text-transform: uppercase;
    text-align: left;
    margin: 0.625rem 0;
    font-weight: bold;
  }

  .control-label {
    text-align: left;
    margin: 0.625rem 0;
  }

  .control-value {
    white-space: pre-wrap;
  }

  &.download-order-summary {
    text-align: left;
  }

  .v-slide-group__content {
    display: flex;
    flex-direction: row;
  }
}
</style>
